import { Button, shapes, TextField } from '@cimpress/react-components';
import React, { useEffect, useState } from 'react';
import { useCreateWorkflow } from '../../../adapters/use-create-workflow';
import { useWorkflow } from '../../../adapters/use-workflow';
import { useModal } from '../../../contexts/modal-context';
import { Location } from '../../../models/location';
import { createWorkflowDetailsModal } from '../../workflows/WorkflowDetailsModal';
import { Shipment } from '../../../ports/ShipmentsService';
import { UseWorkflowHook, UseWorkflowLogHook } from '../../../ports/WorkflowService';
import { useCurrentLocation } from '../../../store/hooks/useCurrentLocation';
import { AlignItems, FlexBox, FlexDirection } from '../../../utils/css-classes';

// tslint:disable-next-line:import-name
import style from './index.module.css';
import { useWorkflowLog } from '../../../adapters/use-workflow-log';

const { Spinner } = shapes;

export interface VoidShipmentsModalProps {
  shipments: Shipment[];
  hide: (count?: number) => void;
}

// tslint:disable-next-line:function-name
function VoidShipmentsConfirm(
  {
    execute,
    hide,
    shipments,
  }: VoidShipmentsModalProps & {
    execute: (shipments: Shipment[], reason: string) => void;
  },
) {
  const [reason, setReason] = useState('');
  return <>
  <p>
    Are you sure you want to void the following {shipments.length} shipment(s)?
  </p>
  <div className={style.References}>
    {shipments.map(
      shipment => <span key={shipment.id}>{shipment.references[0]}</span>,
    )}
  </div>
  <TextField
    value={reason}
    onChange={e => setReason(e.currentTarget.value)}
    label="Reason"
  />
  <div>
    <Button onClick={hide}>Cancel</Button>&nbsp;
    <Button
      disabled={reason.length === 0}
      type="primary"
      onClick={() => execute(shipments, reason)}
    >
      Void {shipments.length} shipments
    </Button>
  </div>
</>;
}

export function createVoidShipmentsModal(
  {
    useCurrentLocation,
    useWorkflow,
    useWorkflowLog,
  }: {
    useCurrentLocation: () => Location;
    useWorkflow: UseWorkflowHook;
    useWorkflowLog: UseWorkflowLogHook;
  },
) {
  // tslint:disable-next-line:variable-name
  const WorkflowDetailsModal = createWorkflowDetailsModal({ useWorkflow, useCurrentLocation, useWorkflowLog });

  // tslint:disable-next-line:function-name
  return function VoidShipmentsModal(
    {
      shipments,
      hide,
    }: VoidShipmentsModalProps,
  ) {
    const modal = useModal();
    const {
      execute,
      isLoading,
      workflow,
    } = useCreateWorkflow();

    useEffect(
      () => {
        if (workflow) {
          setImmediate(
            () => {
              hide(2);
              setImmediate(() =>  modal(
                'Workflow details',
                () => <WorkflowDetailsModal workflowId={workflow.id}/>,
                {
                  bsSize: 'lg',
                },
              ));
            },
          );
        }
      },
      [workflow],
    );

    if (!isLoading && !workflow) {
      return <VoidShipmentsConfirm
        hide={hide}
        shipments={shipments}
        execute={(shipments, reason) => execute({
          name: 'bulk-shipments-void',
          input: {
            reason,
            shipments: shipments.map(({ id, references: [displayName] }) => ({ id, displayName })),
          },
        })}
      />;
    }

    return <div className={`${FlexBox.FLEX} ${FlexDirection.COLUMN} ${AlignItems.CENTER}`}>
      <Spinner />
      <h6>Creating workflow</h6>
    </div>;
  };
}

// tslint:disable-next-line:variable-name
export const VoidShipmentsModal = createVoidShipmentsModal({ useCurrentLocation, useWorkflow, useWorkflowLog });
